import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface CategoriaFormData {
  nome: string;
  descricao?: string;
}

interface CategoriaFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  level: 1 | 2 | 3;
  categoria?: any;
  selectedN1?: any;
  selectedN2?: any;
}

// Categoria Form Modal Component
export function CategoriaFormModal({ 
  open, 
  onOpenChange, 
  onSuccess, 
  level,
  categoria,
  selectedN1,
  selectedN2
}: CategoriaFormModalProps) {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<CategoriaFormData>();

  useEffect(() => {
    if (open) {
      if (categoria) {
        setValue('nome', categoria.nome);
        setValue('descricao', categoria.descricao || '');
      } else {
        reset();
      }
    }
  }, [open, categoria, setValue, reset]);

  const getLevelInfo = () => {
    switch (level) {
      case 1:
        return { 
          title: categoria ? 'Editar Categoria Nível 1' : 'Nova Categoria Nível 1',
          table: 'categorias_n1'
        };
      case 2:
        return { 
          title: categoria ? 'Editar Categoria Nível 2' : 'Nova Categoria Nível 2',
          table: 'categorias_n2'
        };
      case 3:
        return { 
          title: categoria ? 'Editar Categoria Nível 3' : 'Nova Categoria Nível 3',
          table: 'categorias_n3'
        };
    }
  };

  const onSubmit = async (data: CategoriaFormData) => {
    setLoading(true);
    try {
      const { table } = getLevelInfo();
      const user = await supabase.auth.getUser();
      
      let categoriaData: any = {
        nome: data.nome.trim(),
        descricao: data.descricao?.trim() || null,
        updated_by: user.data.user?.id,
      };

      // Add parent references
      if (level === 2) {
        if (!selectedN1) {
          throw new Error('Nenhuma categoria de Nível 1 selecionada');
        }
        categoriaData.id_categoria_n1 = selectedN1.id;
      } else if (level === 3) {
        if (!selectedN2) {
          throw new Error('Nenhuma categoria de Nível 2 selecionada');
        }
        categoriaData.id_categoria_n2 = selectedN2.id;
      }

      if (categoria?.id) {
        // Update existing
        let query;
        if (level === 1) {
          query = supabase.from('categorias_n1');
        } else if (level === 2) {
          query = supabase.from('categorias_n2');
        } else {
          query = supabase.from('categorias_n3');
        }
        
        const { error } = await query
          .update(categoriaData)
          .eq('id', categoria.id);

        if (error) {
          if (error.code === '23505') {
            throw new Error('Já existe uma categoria com este nome neste nível');
          }
          throw error;
        }

        toast({
          title: "Sucesso",
          description: "Categoria atualizada com sucesso!",
        });
      } else {
        // Create new
        categoriaData.created_by = user.data.user?.id;
        
        let insertQuery;
        if (level === 1) {
          insertQuery = supabase.from('categorias_n1');
        } else if (level === 2) {
          insertQuery = supabase.from('categorias_n2');
        } else {
          insertQuery = supabase.from('categorias_n3');
        }
        
        const { error } = await insertQuery.insert([categoriaData]);

        if (error) {
          if (error.code === '23505') {
            throw new Error('Já existe uma categoria com este nome neste nível');
          }
          throw error;
        }

        toast({
          title: "Sucesso",
          description: "Categoria criada com sucesso!",
        });
      }

      onSuccess();
      onOpenChange(false);
      reset();
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const { title } = getLevelInfo();

  const getParentInfo = () => {
    if (level === 2 && selectedN1) {
      return `Categoria pai: ${selectedN1.nome}`;
    }
    if (level === 3 && selectedN1 && selectedN2) {
      return `Categoria pai: ${selectedN1.nome} > ${selectedN2.nome}`;
    }
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {categoria 
              ? 'Edite as informações da categoria.' 
              : 'Adicione uma nova categoria.'
            }
            {getParentInfo() && (
              <div className="mt-2 text-sm bg-muted p-2 rounded">
                {getParentInfo()}
              </div>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nome">Nome *</Label>
            <Input
              id="nome"
              {...register('nome', { 
                required: 'Nome é obrigatório',
                validate: {
                  notEmpty: (value) => value.trim() !== '' || 'Nome não pode estar vazio',
                  maxLength: (value) => value.length <= 100 || 'Nome deve ter no máximo 100 caracteres'
                }
              })}
              placeholder="Nome da categoria"
            />
            {errors.nome && (
              <p className="text-sm text-red-500">{errors.nome.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="descricao">Descrição</Label>
            <Textarea
              id="descricao"
              {...register('descricao', {
                validate: {
                  maxLength: (value) => !value || value.length <= 500 || 'Descrição deve ter no máximo 500 caracteres'
                }
              })}
              placeholder="Descrição da categoria (opcional)"
              rows={3}
            />
            {errors.descricao && (
              <p className="text-sm text-red-500">{errors.descricao.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Salvando...' : (categoria ? 'Atualizar' : 'Criar')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}